import { useState } from 'react'

import { useFormik } from 'formik'
import type { NextPage } from 'next'
import Link from 'next/link'
import { AlertCircle } from 'react-feather'
import { Alert, CardText, Form, Label, Input, Button, FormFeedback } from 'reactstrap'

import LoginLayout from '@/components/layouts/login_layout'
import PageTitle from '@/components/page_title'
import { postForgotPassword } from '@/utils/api/authentication'

interface FormValues {
  email: string
}

const initialValues: FormValues = { email: '' }

const ForgotPassword: NextPage = () => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [baseError, setBaseError] = useState<string | null>(null)

  const validate = (values: FormValues) => {
    const errors = {} as { email?: string }

    if (!values.email) {
      errors.email = 'Required'
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
      errors.email = 'Invalid email address'
    }

    return errors
  }

  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validate,
    onSubmit: (values) => {
      postForgotPassword(values.email)
        .then(() => {
          formik.setStatus({ success: true })
          formik.setSubmitting(false)
          setIsSubmitted(true)
        })
        .catch(() => {
          formik.setStatus({ success: false })
          formik.setSubmitting(false)
          setIsSubmitted(true)
          setBaseError('Oops, something went wrong.')
        })
    },
  })

  return (
    <LoginLayout>
      <PageTitle title="Reset password" />
      {baseError && (
        <Alert className="p-1 d-flex justify-content-between" color="danger">
          Something went wrong.
          <AlertCircle size={18} />
        </Alert>
      )}
      {!isSubmitted && (
        <div>
          <CardText className="mb-2" tag="h3">
            Recover password
          </CardText>
          <Form className="auth-login-form mt-2" onSubmit={formik.handleSubmit}>
            <div className="mb-1">
              <Label className="form-label" for="email">
                Email
              </Label>
              <Input
                autoFocus
                id="email"
                invalid={!!formik.errors.email}
                name="email"
                onChange={formik.handleChange}
                placeholder="john@example.com"
                tabIndex={-1}
                value={formik.values.email}
              />
              <FormFeedback>{formik.errors.email}</FormFeedback>
            </div>

            <Button block className="mb-1" color="primary" disabled={formik.isSubmitting} to="/">
              Send me instructions
            </Button>
            <small>
              <Link href="/sign-in">Back</Link>
            </small>
          </Form>
        </div>
      )}

      {isSubmitted && (
        <div>
          <CardText className="text-center">
            Please follow instructions we have sent you to your email address.
            <br />
            It may take up to 5 minutes for the email to be received.
          </CardText>
          <Link href="/sign-in" passHref>
            <Button block color="primary" disabled={formik.isSubmitting}>
              Back to Sign in
            </Button>
          </Link>
        </div>
      )}
    </LoginLayout>
  )
}

export default ForgotPassword
